import { GUID, isIElementGenericImgSheet } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "./ielements-slice-selector";
import {
  Measurement,
  MeasurementsRecord,
  MeasurementToolState,
  OnlyMeasurementToolStore,
  PanoMeasurementToolState,
  PanoMeasurementWall,
} from "./measurement-tool-slice";
import { curryAppSelector } from "./reselect";

/**
 *  @returns the list of all 3D measurements in current projects
 *  @param state The Root State
 */
export const selectAll3DMeasurements = createSelector(
  [
    selectIElementsSlice,
    (state: OnlyIElementsStore & OnlyMeasurementToolStore) =>
      state.measurementTool.measurements,
  ],
  (iElements, measurements: MeasurementsRecord) => {
    const measurements3D: Measurement[] = [];
    for (const key of Object.keys(measurements)) {
      const is2dImage = isIElementGenericImgSheet(
        selectIElement(key)({ iElements }),
      );
      if (!is2dImage) {
        const dataArray = measurements[key];
        measurements3D.push(...dataArray);
      }
    }

    return measurements3D;
  },
);

/**
 * @param state The current state of the store
 * @returns The measurement tool slice of the store
 */
export function selectMeasurementToolSlice(
  state: OnlyMeasurementToolStore,
): MeasurementToolState {
  return state.measurementTool;
}

/**
 *  @returns the list of measurements associated to an element
 *  @param state The Root State of the app store
 *  @param id The id of the element which measurements we want to get
 */
export const selectMeasurements = curryAppSelector(
  createSelector(
    [
      (state: OnlyMeasurementToolStore) => state.measurementTool.measurements,
      (state: OnlyMeasurementToolStore, id: GUID | GUID[] | undefined) => id,
    ],
    computeMeasurements,
  ),
);

function computeMeasurements(
  measurements: MeasurementsRecord,
  id: GUID | GUID[] | undefined,
): Measurement[] {
  if (!id) return [];

  if (Array.isArray(id)) {
    return id.reduce((result: Measurement[], iElementId) => {
      if (iElementId in measurements) {
        result.push(...measurements[iElementId]);
      }
      return result;
    }, []);
  }

  return id in measurements ? measurements[id] : [];
}

/**
 * @returns the id of the active measurement or undefined if none
 * @param root The Root State
 */
export function selectActiveMeasurement(
  root: OnlyMeasurementToolStore,
): Measurement | undefined {
  return root.measurementTool.activeMeasurement;
}

/**
 * @returns true if a measurement is currently being taken
 * @param root The Root State
 */
export function selectIsMeasurementBeingTaken(
  root: OnlyMeasurementToolStore,
): boolean {
  return root.measurementTool.isMeasurementBeingTaken;
}

/**
 * @returns the number of points placed in the measurement being taken
 * @param root The Root State
 */
export function selectLivePointsCount(root: OnlyMeasurementToolStore): number {
  return root.measurementTool.livePointsCount;
}

/**
 * @returns true while SHIFT locks the next point of the measurement being taken to an axis
 * @param root The Root State
 */
export function selectIsAxisLocked(root: OnlyMeasurementToolStore): boolean {
  return root.measurementTool.isAxisLocked;
}

/**
 *  @returns true if the measurement is a loop
 *  @param id The id of the element which contains the measurement in consideration
 *  @param measurementId The ID of the measurement
 */
export function selectIsMeasurementLoop(id: GUID, measurementId: GUID) {
  return (state: OnlyMeasurementToolStore) => {
    const measurements = state.measurementTool.measurements[id];
    return measurements.find((m) => m.id === measurementId)?.metadata.isLoop;
  };
}

/**
 *  @returns the components to be displayed for this measurement
 *  @param id The id of the element which contains the measurement in consideration
 *  @param measurementId The ID of the measurement
 */
export function selectMeasurementComponentsToDisplay(
  id: GUID,
  measurementId: GUID,
) {
  return (state: OnlyMeasurementToolStore) => {
    const measurements = state.measurementTool.measurements[id];
    return measurements.find((m) => m.id === measurementId)
      ?.componentsToDisplay;
  };
}

/**
 * @param state current app state
 * @returns the number of measurements taken in this browser session
 */
export function selectSessionMeasurements(
  state: OnlyMeasurementToolStore,
): number {
  return state.measurementTool.sessionMeasurements;
}

/**
 * @param id The id of the pano walls associated with
 * @returns the pano measurement walls
 */
export function selectPanoMeasurementWalls(id: GUID) {
  return (state: OnlyMeasurementToolStore): PanoMeasurementWall[] =>
    id in state.measurementTool.panoMeasurementWalls
      ? state.measurementTool.panoMeasurementWalls[id]
      : [];
}

/**
 * @param state current app state
 * @returns the pano measurement tool state
 */
export function selectPanoMeasurementToolState(
  state: OnlyMeasurementToolStore,
): PanoMeasurementToolState {
  return state.measurementTool.panoMeasurementToolState;
}

/**
 * @param panoId The id of the pano
 * @returns the camera height
 */
export function selectCameraHeight(panoId: GUID | undefined) {
  return (state: OnlyMeasurementToolStore): number | undefined => {
    if (panoId !== undefined) {
      return state.measurementTool.cameraHeight[panoId];
    }
  };
}

/**
 *
 * @param state The current app state
 * @returns Whether the zoom lens is active
 */
export function selectZoomLensActive(state: OnlyMeasurementToolStore): boolean {
  return state.measurementTool.isZoomLensActive;
}
